import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import DashboardHome from '@/components/dashboard/DashboardHome';
import Market from '@/pages/Market';

import { DemoAccountsManager } from '@/components/demo/DemoAccountsManager';
import Marketplace from './Marketplace';
import BrokerDashboard from './BrokerDashboard';
import AccountsPage from './AccountsPage';
import StrategyManager from '@/components/strategy/StrategyManager';
import BacktestCards from '@/components/backtest/BacktestCards';
import NotificationOverlay from '@/components/notifications/NotificationOverlay';
import TradingBotDashboard from '@/components/trading-bot/TradingBotDashboard';
import SettingsPage from '@/components/dashboard/SettingsPage';
import ProfilePage from '@/components/dashboard/ProfilePage';
import SupportPage from '@/components/dashboard/SupportPage';
import SignalReviewPage from '@/components/signals/SignalReviewPage';
import PricingPage from '@/pages/PricingPage';
import StrategyAnalyticsPage from '@/components/strategy/StrategyAnalyticsPage';
import TradeJournal from '@/components/trading/TradeJournal';
import PortfolioPage from '@/pages/PortfolioPage';
import AdminHealthCheck from '@/components/dashboard/AdminHealthCheck';

interface DashboardProps {
  onLogout: () => void;
  defaultTab?: string;
}

const Dashboard = ({ onLogout, defaultTab }: DashboardProps) => {
  const location = useLocation();
  const initialTab = (location.state as any)?.defaultTab || defaultTab || 'dashboard';
  const [currentPage, setCurrentPage] = useState(initialTab);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>('');

  const [notifications] = useState<any[]>([]);

  const { user, updateUserMetadata } = useAuth();

  // Track marketplace visit for onboarding
  useEffect(() => {
    if (currentPage !== 'marketplace' || !user) return;
    const steps: string[] = user.user_metadata?.onboarding_completed_steps || [];
    if (!steps.includes('marketplace_visited') && user.user_metadata?.onboarding_auto_setup) {
      updateUserMetadata({ onboarding_completed_steps: [...steps, 'marketplace_visited'] });
    }
  }, [currentPage, user]);

  useEffect(() => {
    const handleOpenNotifications = () => {
      setShowNotifications(true);
    };

    window.addEventListener('openNotifications', handleOpenNotifications);
    return () => {
      window.removeEventListener('openNotifications', handleOpenNotifications);
    };
  }, []);

  const handleMarkRead = (_id: string) => {
    // Handle marking notification as read
  };

  const handleMarkAllRead = () => {
    // Handle marking all notifications as read
  };

  const handleNotificationClick = (notification: any) => {
    if (notification.actionUrl) {
      const url = notification.actionUrl as string;
      if (url.startsWith('http')) {
        window.open(url, '_blank');
      } else if (url.startsWith('/')) {
        setCurrentPage(url.replace('/', ''));
      }
      setShowNotifications(false);
    }
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardHome onPageChange={setCurrentPage} />;
      case 'portfolio':
        return <PortfolioPage />;
      case 'market':
        return <Market />;
      case 'strategies':
        return <StrategyManager onViewAnalytics={(id: string) => { setSelectedStrategyId(id); setCurrentPage('strategy-analytics'); }} />;
      case 'strategy-analytics':
        return <StrategyAnalyticsPage instanceId={selectedStrategyId} onBack={() => setCurrentPage('strategies')} />;
      case 'signals':
        return <SignalReviewPage />;
      case 'accounts':
        return <AccountsPage onPageChange={setCurrentPage} />;
      case 'marketplace':
        return <Marketplace />;
      case 'journal':
        return <TradeJournal />;
      case 'backtest':
        return <BacktestCards />;
      case 'trading-bots':
        return <TradingBotDashboard />;
      case 'brokers':
        return <BrokerDashboard />;
      case 'settings':
        return <SettingsPage />;
      case 'profile':
        return <ProfilePage />;
      case 'support':
        return <SupportPage />;
      case 'pricing':
        return <PricingPage />;
      case 'admin':
        return <AdminHealthCheck />;
      default:
        return <DashboardHome />;
    }
  };

  return (
    <>
      <DashboardLayout
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onLogout={onLogout}
      >
        {renderCurrentPage()}
      </DashboardLayout>

      <NotificationOverlay
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
        onNotificationClick={handleNotificationClick}
      />
    </>
  );
};

export default Dashboard;